import { UseQueryOptions } from '@tanstack/react-query';
import {
	createFlowPath,
	CreateFlowPayload,
	CreateFlowResult,
	FlowConfig,
	FlowSavePayload,
	FlowSaveResult,
	FlowTrigger,
	FlowVersion,
	getAllStubsByReferenceIDsPath,
	getFlowVersionsPath,
	getTriggerTaskPagePath,
	ItemPage,
	MicrotaskType,
	promoteIntegrationPath,
	PromoteIntegrationPayload,
	saveBulkPath,
	urlCombine,
} from 'ihub/common';
import { CRUDHelperBase } from '../classes/crud-helper-base';
import {
	buildURL,
	coreAPI,
	getTenantID,
	handleResponse,
	isURLSet,
	standardGet,
	standardPost,
	validateOkResponse,
} from '../classes/http-helper-const';
import Parameter from '../classes/query-parameter';
import { cacheStaleTime } from '../constants';
import HttpResponseOptions from '../interface/http-response-options';
import QueryClientWrapper from '../utils/create-query-client';

export class FlowHttpService extends CRUDHelperBase<FlowConfig> {
	constructor() {
		super(FlowConfig.name);
	}

	async createFlow(
		item: CreateFlowPayload,
		options?: {
			success?: (response?: CreateFlowResult) => void | Promise<void>;
			error?: (data: any) => void | Promise<void>;
		}
	) {
		isURLSet();
		const url = urlCombine(coreAPI, this.objectType, createFlowPath);
		const response = await standardPost(url, item);
		await handleResponse(response, 'Create Flow', this.objectType, options);
		await super.invalidateKeys();
		await this.invalidateTriggerKey();
	}

	getFlowVersions(
		referenceId: string,
		queryParams?: Parameter[]
	): UseQueryOptions<FlowVersion[], Error, FlowVersion[]> {
		return {
			queryKey: [
				getTenantID(),
				this.objectType,
				getFlowVersionsPath,
				referenceId,
				queryParams?.map(x => `${x.key}=${x.value}`).join('&'),
			],
			queryFn: async (): Promise<FlowVersion[]> => {
				const url = buildURL(FlowConfig.name, [getFlowVersionsPath, referenceId], queryParams);
				const res = await standardGet(url);
				await validateOkResponse(res, 'get flow versions', this.objectType);
				return await res.clone().json();
			},
			staleTime: cacheStaleTime,
		};
	}

	async invalidateGetFlowVersions() {
		const key = [getTenantID(), this.objectType, getFlowVersionsPath];
		await QueryClientWrapper.InvalidateKey(key);
	}

	getAllStubsByReferenceIDs(
		referenceIds: string[],
		queryParams?: Parameter[]
	): UseQueryOptions<FlowConfig[], Error, FlowConfig[]> {
		return {
			queryKey: [
				getTenantID(),
				this.objectType,
				getAllStubsByReferenceIDsPath,
				referenceIds,
				queryParams?.map(x => `${x.key}=${x.value}`).join('&'),
			],
			queryFn: async (): Promise<FlowConfig[]> => {
				const url = buildURL(FlowConfig.name, [getAllStubsByReferenceIDsPath], queryParams);
				const res = await standardPost(url, referenceIds);
				await validateOkResponse(res, 'get all reference IDs', this.objectType);
				return await res.clone().json();
			},
			staleTime: cacheStaleTime,
		};
	}

	async invalidateGetAllByReferenceIDs() {
		const key = [getTenantID(), this.objectType, getAllStubsByReferenceIDsPath];
		return await QueryClientWrapper.InvalidateKey(key);
	}

	async invalidateFlowVersionKey(referenceId: string): Promise<void> {
		await super.invalidateKeys();
		await this.invalidateTriggerKey();
		await QueryClientWrapper.InvalidateKey([
			getTenantID(),
			this.objectType,
			getFlowVersionsPath,
			referenceId,
		]);
	}

	async saveBulk(
		item: FlowSavePayload,
		options?: {
			success?: (response: FlowSaveResult) => void | Promise<void>;
			error?: (data: any) => void | Promise<void>;
		}
	) {
		isURLSet();
		const url = urlCombine(coreAPI, this.objectType, saveBulkPath);
		const response = await standardPost(url, item);
		await handleResponse(response, 'Save Bulk', this.objectType, options);
		await super.invalidateKeys();
		await this.invalidateTriggerKey();
	}

	GetTriggerTaskPage(
		triggerType: MicrotaskType,
		limit: number,
		offset: number,
		queryParams?: Parameter[]
	): UseQueryOptions<ItemPage<FlowTrigger>, Error, ItemPage<FlowTrigger>> {
		return {
			queryKey: [
				getTenantID(),
				this.objectType,
				getTriggerTaskPagePath,
				triggerType,
				limit.toString(),
				offset.toString(),
				queryParams?.map(x => `${x.key}=${x.value}`).join('&'),
			],
			queryFn: async (): Promise<ItemPage<FlowTrigger>> => {
				const url = buildURL(
					FlowConfig.name,
					[getTriggerTaskPagePath, triggerType.toString(), limit.toString(), offset.toString()],
					queryParams
				);
				const res = await standardGet(url);
				await validateOkResponse(res, 'get trigger task page', this.objectType);
				return await res.clone().json();
			},
			staleTime: cacheStaleTime,
		};
	}

	override async deleteAll(
		itemIds: string[],
		options?: HttpResponseOptions,
		queryParams?: Parameter[],
		remove = true
	) {
		await super.deleteAll(itemIds, options, queryParams, remove);
		await super.invalidateKeys(undefined, remove);
		await QueryClientWrapper.InvalidateKey(
			[getTenantID(), this.objectType, getTriggerTaskPagePath],
			remove
		);
	}

	override async delete(
		itemId: string,
		options?: HttpResponseOptions,
		queryParams?: Parameter[],
		additionalHeaders?: HeadersInit,
		remove = true
	) {
		await super.delete(itemId, options, queryParams, additionalHeaders, remove);
		await super.invalidateKeys(undefined, remove);
		await QueryClientWrapper.InvalidateKey(
			[getTenantID(), this.objectType, getTriggerTaskPagePath],
			remove
		);
	}

	async promoteIntegration(
		promoteIntegrationPayload: PromoteIntegrationPayload,
		options?: {
			success?: (response?: any) => void | Promise<void>;
			error?: (data: any) => void | Promise<void>;
		}
	) {
		isURLSet();
		const url = urlCombine(coreAPI, this.objectType, promoteIntegrationPath);
		const response = await standardPost(url, promoteIntegrationPayload);
		await handleResponse(response, 'Promote Integration', this.objectType, options);
		await super.invalidateKeys();
		await this.invalidateTriggerKey();
	}

	async invalidateTriggerKey() {
		await QueryClientWrapper.InvalidateKey([
			getTenantID(),
			this.objectType,
			getTriggerTaskPagePath,
		]);
	}
}
